import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { t } from '../utilities/translator';
import { selectGlobalLoading } from '../appSlice';
import { selectBestOutOfTotal, selectDealsList } from '../features/dealsList/dealsListSlice';
import { selectFilterData } from '../features/mainFilter/selectors';
import { multiplyBestOutOf } from '../utilities/helpers';
import abTesting from '../services/abTesting';
import PersonalizedTitle from '../features/dealsList/PersonalizedTitle';
import { selectActiveSort } from '../features/secondaryFilter/secondaryFilterSlice';

export default function MainTitleComponent({ destinationName }) {
  const filtersInteractionCount = useSelector((state) => state.counters.filtersInteractionCount);
  const search = useSelector((state) => state.userSearch);
  const { dealListLoading, secondaryFilterLoading } = useSelector(selectGlobalLoading);
  const { pending } = useSelector(selectDealsList);
  const bestOutOfTotal = useSelector(selectBestOutOfTotal);
  const mainFilter = useSelector(selectFilterData);
  const activeSort = useSelector(selectActiveSort);

  const { where, what, when } = mainFilter.searchEngine;
  const selectedDestinations = where?.destinations.filter(({ active }) => active);
  const selectedDestinationsGroups = where?.destinationsGroups.filter(({ active }) => active);
  const selectedThemes = what?.filter(({ active }) => active);

  let totalBestOutOf = multiplyBestOutOf(when, 'multiplyTotal', bestOutOfTotal);
  totalBestOutOf = totalBestOutOf.toLocaleString('en-US', { maximumFractionDigits: 2 });

  const renderBestOutOf = useMemo(() => {
    if (filtersInteractionCount <= 6 || !bestOutOfTotal) return null;
    if (selectedDestinations.length === 1) return null;

    if (dealListLoading || search.showLoader || pending) {
      return <span className="results-section-title__bestOutOf"></span>;
    }
    return (
      <span className="results-section-title__bestOutOf">
        {t('app.offers_scored_for_this_search', { totalBestOutOf })}
      </span>
    );
  }, [filtersInteractionCount, bestOutOfTotal, dealListLoading, search.showLoader, pending, totalBestOutOf, selectedDestinations]);

  const getTitle = () => {
    if (selectedDestinations.length === 1) {
      return {
        default: t('app.these_are_the_best_deals_we_found', { destinationName }),
        personalized: t('app.personalizedSingleDestination', { destinationName }),
      }
    }
    if (selectedThemes.length === 1) {
      return {
        default: t('app.these_are_the_best_deals_we_found_for_each_destination', { name: selectedThemes[0].value }),
        personalized: t('app.personalizedSingleTheme', { name: selectedThemes[0].value, }),
      };
    }
    if (selectedDestinationsGroups.length === 1) {
      return {
        default: t('app.these_are_the_best_deals_we_found_for_each_destination', { name: selectedDestinationsGroups[0].name }),
        personalized: t('app.personalizedSingleDestinationGroup', { name: selectedDestinationsGroups[0].name }),
      }
    }
    return {
      default: t('app.these_are_the_best_deals_we_found_for_each_destination_general'),
      personalized: t('app.personalizedGeneral'),
    };
  }

  if (dealListLoading) {
    return (
      <h2 className={`results-section-title ${secondaryFilterLoading ? 'marginTop' : ''}`}>
        <div className="results-section-title__skeleton" />
      </h2>
    );
  }

  if (abTesting.userPrefEnabled && activeSort.label === 'user_pref') {
    return (
      <PersonalizedTitle text={getTitle().personalized} />
    )
  }

  return (
    <div className="personalized-deals-title">
      <div className="results-section-title__text-wrap">
        {renderBestOutOf}
        <span
          className="text-2xl font-semibold leading-none"
          dangerouslySetInnerHTML={{ __html: getTitle().default }}
        />
      </div>
    </div>
  );
}
